import './Spoofing.scss';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { DataFetcher } from 'components/DataFetcher';
import { getFetchOptions } from "utils/fetch";
import SearchBox from '../Search/SearchBox';
import SelectableList from './components/SelectableList';
import { Button, ButtonType } from '../../components/Button/Button';
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSpoofing } from "./hooks/useSpoofing";
import useUserData from "hooks/useUserData";

const Spoofing = ({ toggle, currentUser }) => {

	const {
		spoofedUser,
		checkSpoofing,
		handleStartSpoofingClick,
		handleStopSpoofingClick
	} = useSpoofing();

	const { userData } = useUserData();

	//Searchbox Values
	const [selectedUser, setSelectedUser] = useState();
	const [selectedCompany, setSelectedCompany] = useState();
	const [companyToSpoof, setCompanyToSpoof] = useState();
	const [companySearchTerm, setCompanySearchTerm] = useState("");
	const [emailSearchTerm, setEmailSearchTerm] = useState("");
	const [companyUserSearchTerm, setCompanyUserSearchTerm] = useState("");

	// Results
	const [companyResults, setCompanyResults] = useState([]);
	const [userResults, setUserResults] = useState([]);
	const [userEmailResults, setUserEmailResults] = useState([]);

	// Fetch control
	const [fetchType, setFetchType] = useState(null);
	const [fetchData, setFetchData] = useState(null);
	const [triggerFetch, setTriggerFetch] = useState(false);

	const handleCompanySearch = (companySearchTerm) => {
		const trimmed = companySearchTerm.trim();
		setCompanySearchTerm(trimmed);

		setSelectedCompany();
		setSelectedUser();

		if (!trimmed) {
			setCompanyResults([]);
			return;
		}

		setFetchData({
			url: `/api/v3/spoofingdata/companies?query=${encodeURIComponent(trimmed)}`,
			options: getFetchOptions({ method: "GET" }),
		});
		setFetchType("company");
		setTriggerFetch(true);
	};

	const handleUserSearch = (userSearchCompanyId) => {
		setFetchData({
			url: `/api/v3/spoofingdata/companies/${encodeURIComponent(userSearchCompanyId)}/users`,
			options: getFetchOptions({ method: "GET" }),
		});
		setFetchType('userCompany');
		setTriggerFetch(true);
	};

	const handleUserEmailSearch = (userEmailSearchTerm) => {
		const trimmed = userEmailSearchTerm.trim();
		setEmailSearchTerm(trimmed);

		setSelectedCompany();
		setSelectedUser();

		if (!trimmed) {
			setUserEmailResults([]);
			return;
		}

		setFetchData({
			url: `/api/v3/spoofingdata/users?query=${encodeURIComponent(trimmed)}`,
			options: getFetchOptions({ method: "GET" }),
		});
		setFetchType("userEmail");
		setTriggerFetch(true);
	};

	const handleSetData = useCallback((data) => {
		switch (fetchType) {
			case 'company':
				setCompanyResults(data);
				break;
			case 'userCompany':
				setUserResults(data);
				break;
			case 'userEmail':
				setUserEmailResults(
					data.map(user => ({
						...user,
						Email: decodeURIComponent(user.Email)
					}))
				);
				break;
			default:
				break;
		}
		setTriggerFetch(false);
		setFetchType(null);
	}, [fetchType]);

	useEffect(() => {
		checkSpoofing();
		window.addEventListener("storage", checkSpoofing);
		return () => {
			window.removeEventListener("storage", checkSpoofing);
		};
	}, [checkSpoofing]);

	useEffect(() => {
		if (spoofedUser && userData?.Company && userData?.CompanyId) {
			const runLogic = () => {
				setCompanyToSpoof({
					Name: userData.Company,
					Id: userData.CompanyId
				});
				handleUserSearch(userData.CompanyId);
				handleUserEmailSearch("");
			};

			if (!toggle) {
				const timeout = setTimeout(runLogic, 500);
				return () => {
					clearTimeout(timeout); // Clean up if deps change
				};
			} else {
				runLogic();
			}
		}
	}, [spoofedUser, userData, toggle]);

	const handleCompanyClick = () => {
		setCompanyToSpoof(selectedCompany);
		handleUserSearch(selectedCompany.Id);
	};

	const handleCancelCompanyClick = () => {
		setCompanyToSpoof();
		setSelectedCompany();
		setCompanySearchTerm("");
		setSelectedUser();
		setCompanyResults([]);
	};

	const handleCompanyUserSearch = (searchTerm) => {
		setCompanyUserSearchTerm(searchTerm.trim().toLowerCase());
	};

	const filteredUserResults = useMemo(() => {
		if (!companyUserSearchTerm) {
			return userResults;
		}
		return userResults.filter((user) =>
			`${user.FullName ?? ""} ${user.Email ?? ""}`.toLocaleLowerCase().includes(companyUserSearchTerm)
		);
	}, [userResults, companyUserSearchTerm]);

	const groupedAndFlattenedUsers = useMemo(() => {
		const grouped = userEmailResults.reduce((total, user) => {
			const company = user.CompanyName || "Unknown Company";
			if (!total[company]) {
				total[company] = [];
			}
			total[company].push(user);
			return total;
		}, {});

		Object.values(grouped).forEach(users =>
			users.sort((a, b) => a.FullName.localeCompare(b.FullName))
		);

		return Object.entries(grouped).flatMap(([company, users]) => [
			{ type: "header", company },
			...users.map(u => ({ type: "user", ...u }))
		]);
	}, [userEmailResults]);

	return (
		<div className={`spoofing ${toggle ? "open" : "closed"} ${spoofedUser ? "is-spoofing" : ""}`}>
			{spoofedUser ? <h2>{currentUser.Name}</h2> : <h2>Spoofing</h2>}

			{triggerFetch && fetchData && (
				<DataFetcher fetchData={fetchData} setData={handleSetData} />
			)}

			{(userEmailResults.length < 1 && !companyToSpoof) && (
				<div className="search-companies">
					<h3>Search Companies</h3>
					<div className="search-input-row">
						<SearchBox handleKeyDown={handleCompanySearch} />
					</div>

					{companyResults.length > 0 ? (
						<SelectableList data={companyResults} onSelectItem={setSelectedCompany} />
					) : (
						companySearchTerm && <p className="no-results">No matching companies found.</p>
					)}
				</div>
			)}

			{companyToSpoof &&
				<div className="search-users">
					<h3>{companyToSpoof.Name} <span className="close-company" onClick={handleCancelCompanyClick}><FontAwesomeIcon icon={faXmark} /></span></h3>
					{userResults.length > 4 && (
						<SearchBox placeholder="Filter Users" handleKeyDown={handleCompanyUserSearch} isFilter />
					)}
					{filteredUserResults.length > 0 ? (
						<>

							<div className="admin-key"><span className="admin-key-icon"><p>A</p></span> <p>- Admin Account</p></div>
							<SelectableList
								data={filteredUserResults.map(user => ({ type: "user", ...user }))}
								onSelectItem={setSelectedUser}
							/>
						</>

					) : (
						<p className="no-results">No matching users found.</p>
					)}
				</div>
			}

			{(companyResults.length < 1 && !companyToSpoof) && (
				<div className="search-email">
					<h3>Search by Email</h3>
					<div className="search-input-row">
						<SearchBox handleKeyDown={handleUserEmailSearch} />
					</div>

					{userEmailResults.length > 0 ? (
						<>
							<div className="admin-key"><span className="admin-key-icon"><p>A</p></span> <p>- Admin Account</p></div>
							<SelectableList data={groupedAndFlattenedUsers} onSelectItem={setSelectedUser} />
						</>
					) : (
						emailSearchTerm && <p className="no-results">No matching users found.</p>
					)}
				</div>
			)}

			{(selectedCompany && !companyToSpoof) || selectedUser || spoofedUser ? (
				<div className="spoofing-buttons">
					<h3>Spoofing Controls</h3>
					{(selectedCompany && !companyToSpoof) && (
						<Button
							type={ButtonType.Secondary}
							text={`Select ${selectedCompany.Name}`}
							onClick={handleCompanyClick}
						/>
					)}
					{selectedUser && (
						<Button
							type={ButtonType.Secondary}
							text={`Spoof as ${selectedUser.FullName}`}
							onClick={() => handleStartSpoofingClick(selectedUser)}
						/>
					)}
					{spoofedUser && (
						<Button
							text="Stop Spoofing"
							onClick={handleStopSpoofingClick}
						/>
					)}
				</div>
			) : null}
		</div>
	);
};

export default Spoofing;
